<template>
  <LayoutPage :title="['Management Summary', 'Move In Sources']">
    <template #actions>
      <SwitchRegionalView v-model="regionalView" />
    </template>
    <v-row>
      <v-col cols="12" md="6">
        <RegionAndFacilitySelect />
      </v-col>
      <v-col v-if="!regionalView" cols="12" md="2">
        <v-select
          v-model="selectedMoveInSource"
          :items="moveInSourceOptions"
          label="Move-In Source"
          outlined
          dense
          multiple
          clearable
          clear-icon="far fa-times-circle"
          :menu-props="{ offsetY: true, bottom: true, top: false }"
        >
          <template #prepend-item>
            <v-list-item>
              <v-checkbox
                v-model="selectAllSources"
                @change="toggleSelectAllSources"
              />
              <v-list-item-content>Select All</v-list-item-content>
            </v-list-item>
          </template>
          <template #selection="{ item, index }">
            <div v-if="index === 0" class="d-block text-truncate">
              {{ item.text || item }}
            </div>
            <div v-if="index === 1" class="grey--text caption">
              (+{{ selectedMoveInSource.length - 1 }} others)
            </div>
          </template>
        </v-select>
      </v-col>
      <v-col cols="12" md="2">
        <v-select
          v-model="selectedYear"
          :items="YEAR_OPTIONS"
          label="Year"
          outlined
          dense
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-select
          v-model="selectedMonth"
          :items="MONTH_OPTIONS"
          label="Month"
          outlined
          dense
        />
      </v-col>
      <v-col v-if="regionalView" cols="12" md="2">
        <SearchField v-model="tableState.search" />
      </v-col>
    </v-row>

    <!-- Regional View: aggregated table from MONTHLY_MOVE_IN_SOURCES -->
    <template v-if="regionalView">
      <v-skeleton-loader
        :loading="regionalLoading"
        type="table-thead, table-row-divider@10"
      >
        <v-data-table
          :items="regionalTableData.items"
          :headers="regionalTableData.headers"
          :search="tableState.search"
          :sort-by.sync="tableState.sortBy"
          :sort-desc.sync="tableState.sortDesc"
          :page.sync="tableState.page"
          :items-per-page.sync="tableState.itemsPerPage"
          :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
        >
          <template
            v-for="source in allSources"
            v-slot:[`item.${sourceKey(source)}`]="{ item }"
          >
            <TableCellWrapper :key="source">
              {{ item[sourceKey(source)] || 0 }}
            </TableCellWrapper>
          </template>
          <template #body.append>
            <tr class="table-total">
              <td>Total</td>
              <td></td>
              <td v-for="source in allSources" :key="source">
                <TableCellWrapper>
                  {{ regionalTotals[sourceKey(source)] || 0 }}
                </TableCellWrapper>
              </td>
            </tr>
          </template>
          <template #footer.prepend>
            <ButtonExportCSV
              class="order-last"
              :table-data="regionalCsvData"
              :headers="regionalTableData.headers"
              filename="Move_In_Sources_Report"
            />
            <ButtonPrint
              class="order-last"
              :before-print="showAllItems"
              :after-print="restoreItemsPerPage"
            />
          </template>
        </v-data-table>
      </v-skeleton-loader>
    </template>

    <!-- Facility View: paginated details from MOVE_IN_SOURCE_DETAILS_PAGINATED -->
    <template v-else>
      <v-skeleton-loader
        :loading="facilityLoading"
        type="table-thead, table-row-divider@10"
      >
        <v-data-table
          :items="facilityDetails"
          :headers="facilityHeaders"
          :server-items-length="totalCount"
          :page.sync="page"
          :items-per-page.sync="itemsPerPage"
          :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
        >
          <template #item.moveInSource="{ item }">
            <TableCellWrapper>
              {{ item.moveInSource }}
            </TableCellWrapper>
          </template>
          <template #body.append>
            <tr class="table-total">
              <td>Total</td>
              <td>-</td>
              <td>-</td>
              <td>{{ facilityDetails.length }}</td>
            </tr>
          </template>
          <template #footer.prepend>
            <ButtonExportCSV
              class="order-last"
              :table-data="facilityDetails"
              :headers="facilityHeaders"
              filename="Move_In_Source_Details_Report"
            />
            <ButtonPrint
              class="order-last"
              :before-print="showAllItems"
              :after-print="restoreItemsPerPage"
            />
          </template>
        </v-data-table>
      </v-skeleton-loader>
    </template>
  </LayoutPage>
</template>

<script>
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router/composables";
import LayoutPage from "@layout/LayoutPage.vue";
import RegionAndFacilitySelect from "../RegionAndFacilitySelect.vue";
import SearchField from "@atoms/SearchField.vue";
import SwitchRegionalView from "@atoms/SwitchRegionalView.vue";
import TableCellWrapper from "@layout/TableCellWrapper.vue";
import ButtonExportCSV from "@atoms/ButtonExportCSV.vue";
import ButtonPrint from "@atoms/ButtonPrint.vue";
import useDashboard from "../useDashboard";
import useDataTable from "@components/common/useDataTable";
import useUser from "@components/authentication/useUser";
import { DateTime } from "luxon";
import { getDateParts } from "@tod-ui/helpers/datetime";
import { MONTH_OPTIONS, YEAR_OPTIONS } from "@tod-ui/constants/dashboard";
import {
  MONTHLY_MOVE_IN_SOURCES,
  MOVE_IN_SOURCE_DETAILS_PAGINATED,
} from "./graphql";

export default {
  name: "PageMoveInSourcesReport",
  components: {
    LayoutPage,
    RegionAndFacilitySelect,
    SearchField,
    SwitchRegionalView,
    TableCellWrapper,
    ButtonExportCSV,
    ButtonPrint,
  },
  setup() {
    const { selectedFacilities } = useDashboard();
    const { defaultOrganization } = useUser();
    const { tableState, showAllItems, restoreItemsPerPage } = useDataTable({
      itemsPerPage: 10,
      sortBy: [],
    });

    const selectedYear = ref(
      getDateParts(
        { year: true },
        undefined,
        defaultOrganization.value.timeZone
      )
    );
    const selectedMonth = ref(
      getDateParts(
        { month: true },
        undefined,
        defaultOrganization.value.timeZone
      )
    );
    const selectedDate = computed(
      () => `${selectedYear.value}-${selectedMonth.value}-01`
    );

    const route = useRoute();

    // Parse "April 2026" → year "2026", month "04" from chart bar click
    const parseMonthQuery = (monthStr) => {
      if (!monthStr) return;
      const parsed = DateTime.fromFormat(monthStr, "MMMM yyyy");
      if (!parsed.isValid) return;
      selectedYear.value = String(parsed.year);
      selectedMonth.value = String(parsed.month).padStart(2, "0");
    };

    parseMonthQuery(route.query.month);

    // Pre-select filter from route query when navigating from chart
    const selectedMoveInSource = ref(
      route.query.select_option ? [route.query.select_option] : []
    );

    watch(
      () => route.query.select_option,
      (val) => {
        selectedMoveInSource.value = val ? [val] : [];
      }
    );

    watch(
      () => route.query.month,
      (val) => parseMonthQuery(val)
    );

    return {
      selectedFacilities,
      defaultOrganization,
      tableState,
      showAllItems,
      restoreItemsPerPage,
      selectedYear,
      selectedMonth,
      selectedDate,
      selectedMoveInSource,
      YEAR_OPTIONS,
      MONTH_OPTIONS,
    };
  },
  data() {
    return {
      regionalView: true,
      monthlyMoveInSources: [],
      moveInSourceDetails: null,
      page: 1,
      itemsPerPage: 10,
      selectAllSources: false,
    };
  },
  watch: {
    selectedMoveInSource(val) {
      this.selectAllSources = val.length === this.moveInSourceOptions.length;
      this.page = 1;
    },
    selectedDate() {
      this.page = 1;
    },
    regionalView() {
      this.page = 1;
    },
  },
  computed: {
    regionalLoading() {
      return this.$apollo.queries.monthlyMoveInSources.loading;
    },
    facilityLoading() {
      return this.$apollo.queries.moveInSourceDetails.loading;
    },
    facilitySourcesFilter() {
      const selected = this.selectedMoveInSource;
      if (
        !selected.length ||
        selected.length === this.moveInSourceOptions.length
      )
        return null;
      return selected;
    },
    facilityDetails() {
      return this.moveInSourceDetails?.data || [];
    },
    totalCount() {
      return this.moveInSourceDetails?.page?.totalCount || 0;
    },
    allSources() {
      const sources = new Set(
        this.monthlyMoveInSources.map((d) => d.moveInSource)
      );
      return Array.from(sources).sort();
    },
    moveInSourceOptions() {
      return this.allSources.map((s) => ({ text: s, value: s }));
    },
    facilityHeaders() {
      return [
        { text: "Facility Name", value: "facilityName" },
        { text: "Unit Number", value: "smartUnitNumber" },
        { text: "Move In Date", value: "moveInDate" },
        { text: "Move In Source", value: "moveInSource" },
      ];
    },
    pivotedFacilityData() {
      const filtered = this.monthlyMoveInSources.filter(
        (d) => d.moveInMonth === this.selectedDate
      );

      const facilityMap = {};
      filtered.forEach((d) => {
        const key = d.facilityId;
        if (!facilityMap[key]) {
          facilityMap[key] = {
            facilityId: d.facilityId,
            facilityName: d.facilityName,
            region: d.region,
            organizationPath: d.region,
          };
        }
        facilityMap[key][this.sourceKey(d.moveInSource)] =
          (facilityMap[key][this.sourceKey(d.moveInSource)] || 0) +
          (d.moveInCount || 0);
      });

      return Object.values(facilityMap);
    },
    regionalTableData() {
      const baseHeaders = [
        { text: "Organization/Region Name", value: "organizationPath" },
        { text: "Facility Name", value: "facilityName" },
      ];

      const sourceHeaders = this.allSources.map((s) => ({
        text: s,
        value: this.sourceKey(s),
      }));

      return {
        headers: [...baseHeaders, ...sourceHeaders],
        items: this.pivotedFacilityData,
      };
    },
    regionalTotals() {
      const totals = {};
      this.allSources.forEach((s) => {
        const key = this.sourceKey(s);
        totals[key] = this.regionalTableData.items.reduce(
          (sum, item) => sum + (item[key] || 0),
          0
        );
      });
      return totals;
    },
    regionalCsvData() {
      const totalRow = {
        organizationPath: "Total",
        facilityName: "",
        ...this.regionalTotals,
      };
      return [...this.regionalTableData.items, totalRow];
    },
  },
  apollo: {
    monthlyMoveInSources: {
      query: MONTHLY_MOVE_IN_SOURCES,
      variables() {
        return {
          ids: this.selectedFacilities.map((f) => f.id),
          month: [this.selectedDate],
        };
      },
      update: (data) => data.monthlyMoveInSources,
      skip() {
        return this.selectedFacilities.length === 0;
      },
    },
    moveInSourceDetails: {
      query: MOVE_IN_SOURCE_DETAILS_PAGINATED,
      variables() {
        return {
          ids: this.selectedFacilities.map((f) => f.id),
          month: this.selectedDate,
          moveInSources: this.facilitySourcesFilter,
          page: this.page,
          limit: this.itemsPerPage,
        };
      },
      update: (data) => data.moveInSourceDetailsPaginated,
      skip() {
        return this.selectedFacilities.length === 0 || this.regionalView;
      },
    },
  },
  methods: {
    toggleSelectAllSources(value) {
      this.selectedMoveInSource = value
        ? this.moveInSourceOptions.map((o) => o.value)
        : [];
    },
    sourceKey(source) {
      return source ? source.toLowerCase().replace(/\s+/g, "_") : "unknown";
    },
  },
};
</script>

<style>
@media print {
  @page {
    size: A4 landscape;
  }
}
</style>
